import React, { useState, useMemo, useEffect } from 'react';
import { 
  MagnifyingGlassIcon, 
  PlusIcon, 
  EllipsisVerticalIcon,
  XMarkIcon,
  ClockIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';
import { createEmployee, getAllEmployeesList } from '../services/api';
import ActivityLogs from '../components/ActivityLogs';
import Reports from '../components/Reports'; 

const EmployeeDirectory = () => {
  const [employees, setEmployees] = useState([]); 
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('directory');
  
  // People Management specific activity types
  const activityTypes = [
    "Employee Created", "Employee Updated", "Employee Deactivated", 
    "Employee Onboarded", "Profile Modified", "Department Changed"
  ];

  // People Management specific report types
  const reportTypes = [
    "Employee Directory Report", "Department Wise Report", 
    "Employee Status Report", "Onboarding Report",
    "Employee Demographics Report", "Active Employees Report"
  ];

  const [newEmp, setNewEmp] = useState({
    name: '', 
    email: '', 
    id: '', 
    dept: 'Engineering', 
    role: '', 
    type: 'Full-time', 
    mobile: '', 
    gender: 'Male'
  });

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const data = await getAllEmployeesList();
      const dataList = Array.isArray(data) ? data : (data.employees || []);
      const formattedData = dataList.map(emp => ({
        id: String(emp.employee_id || emp.emp_code || emp.id || ''),
        name: String(emp.full_name || emp.name || emp.first_name || 'Unknown'),
        email: emp.email || 'N/A',
        dept: emp.department || emp.dept || 'N/A', 
        role: emp.designation || emp.role || 'N/A',
        status: emp.is_verified ? 'Active' : 'Pending',
        initials: (emp.full_name || emp.first_name || 'U').charAt(0).toUpperCase(),
      }));
      setEmployees(formattedData);
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchEmployees(); }, []);

  const filteredEmployees = useMemo(() => {
    return employees.filter(emp => {
      const searchTerm = (search || "").toLowerCase();
      return (emp.name || "").toLowerCase().includes(searchTerm) || (emp.id || "").toLowerCase().includes(searchTerm);
    });
  }, [search, employees]);

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Split name into first and last for FastAPI compatibility
    const nameParts = newEmp.name.trim().split(" ");
    const fName = nameParts[0] || "";
    const lName = nameParts.length > 1 ? nameParts.slice(1).join(" ") : "User";

    // UPDATED PAYLOAD: Including both sets of possible keys to avoid 422 error
    const payload = {
      // Keys for the new Onboard logic
      employee_id: newEmp.id.trim(),
      full_name: newEmp.name.trim(),
      
      // Keys often used in standard FastAPI user models
      first_name: fName,
      last_name: lName,
      email: newEmp.email.trim(),
      emp_code: newEmp.id.trim(),
      mobile_number: newEmp.mobile.trim(),
      phone: newEmp.mobile.trim(),
      
      // Organization details
      department: newEmp.dept,
      designation: newEmp.role,
      role: "EMPLOYEE",
      gender: newEmp.gender,
      employee_type: newEmp.type
    };

    try {
      const response = await createEmployee(payload);
      console.log("✅ Server Response:", response);
      
      alert(
        `✅ Success! Employee Onboarded.\n\n` +
        `Credentials:\n` +
        `User: ${payload.email}\n` +
        `Password: Welcome@123\n\n` +
        `Check the inbox of ${payload.email} for the welcome email.`
      );
      
      setIsModalOpen(false);
      setNewEmp({ name: '', email: '', id: '', dept: 'Engineering', role: '', type: 'Full-time', mobile: '', gender: 'Male' });
      fetchEmployees(); 
    } catch (error) {
      // Enhanced error logging to see exactly why FastAPI is failing (the 'detail' field)
      const errorDetail = error.response?.data?.detail;
      const message = typeof errorDetail === 'string' 
        ? errorDetail 
        : "Validation error or User already exists.";
        
      console.error("❌ Request Failed:", error.response?.data);
      alert(`⚠️ Onboarding Error: ${message}\n\nNote: If using a real email, ensure it isn't already registered.`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white p-4 font-roboto text-slate-800">
      <header className="flex justify-between items-center mb-6">
        <div>
          <h1 className="font-poppins text-xl font-bold text-slate-900 tracking-tight">People Management</h1>
          <p className="text-xs text-slate-500 font-medium italic">Directory & Onboarding</p>
        </div>
        <div className="flex items-center gap-3">
          <button 
            onClick={() => setActiveTab('activity')} 
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded transition-all shadow-sm active:scale-95 ${
              activeTab === 'activity' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100 border'
            }`}
          >
            <ClockIcon className="w-4 h-4" /> Activity Logs
          </button>
          
          <button 
            onClick={() => setActiveTab('reports')} 
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded transition-all shadow-sm active:scale-95 ${
              activeTab === 'reports' ? 'bg-purple-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100 border'
            }`}
          >
            <ChartBarIcon className="w-4 h-4" /> Reports
          </button>
          
          <button 
            onClick={() => setActiveTab('directory')} 
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded transition-all shadow-sm active:scale-95 ${
              activeTab === 'directory' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100 border'
            }`}
          >
            <MagnifyingGlassIcon className="w-4 h-4" /> Directory
          </button>
          
          <button 
            onClick={() => setIsModalOpen(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-all shadow-sm active:scale-95"
          >
            <PlusIcon className="w-4 h-4" /> Add Employee
          </button>
        </div>
      </header>

      {/* Activity Logs Tab */}
      {activeTab === 'activity' && (
        <ActivityLogs 
          moduleName="People Management" 
          activityTypes={activityTypes} 
        />
      )}
      
      {/* Reports Tab */}
      {activeTab === 'reports' && (
        <Reports 
          moduleName="People Management" 
          reportTypes={reportTypes} 
        />
      )}

      {/* Directory Tab */}
      {activeTab === 'directory' && (
        <>
      <div className="flex flex-wrap items-center gap-3 mb-4 bg-slate-50 p-2 rounded-lg border border-slate-100">
        <div className="relative flex-1 min-w-[300px]">
          <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input 
            type="text" 
            placeholder="Search by name or Employee ID..." 
            className="w-full pl-9 pr-4 py-1.5 text-xs border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 bg-white" 
            value={search} 
            onChange={(e) => setSearch(e.target.value)} 
          />
        </div>
      </div>

      <div className="border border-slate-200 rounded shadow-sm overflow-hidden bg-white">
        <div className="overflow-x-auto max-h-[calc(100vh-280px)]">
          <table className="w-full text-left border-collapse">
            <thead className="bg-slate-50 sticky top-0 z-20 shadow-sm text-[10px] font-bold text-slate-700 uppercase">
              <tr className="border-b border-slate-200">
                <th className="px-4 py-3 w-10 text-center text-slate-400">#</th>
                <th className="px-3 py-3">User Details</th>
                <th className="px-3 py-3">Employee ID</th>
                <th className="px-3 py-3">Status</th>
                <th className="px-3 py-3 w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredEmployees.map((emp, index) => (
                <tr key={emp.id || index} className="hover:bg-slate-50 transition-colors">
                  <td className="px-4 py-2 text-center text-xs text-slate-300">●</td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-3">
                      <div className="w-7 h-7 flex items-center justify-center rounded-full font-bold text-[10px] bg-indigo-100 text-indigo-700">{emp.initials}</div>
                      <div className="flex flex-col">
                        <span className="text-xs font-bold text-slate-800">{emp.name}</span>
                        <span className="text-[10px] text-slate-400">{emp.email}</span>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2 text-xs font-bold text-slate-600">{emp.id}</td>
                  <td className="px-3 py-2">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${emp.status === 'Active' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                      {emp.status}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button className="p-1 text-slate-400 hover:text-slate-600"><EllipsisVerticalIcon className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
        </>
      )}

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white w-full max-w-md rounded-xl shadow-2xl overflow-hidden flex flex-col max-h-[85vh]">
            <div className="px-6 py-4 border-b flex justify-between items-center bg-slate-50">
              <h2 className="font-poppins font-bold text-slate-800">Add New Employee</h2>
              <button onClick={() => setIsModalOpen(false)}><XMarkIcon className="w-5 h-5 text-slate-400" /></button>
            </div>

            <div className="overflow-y-auto p-6 scrollbar-hide">
              <form onSubmit={handleAddSubmit} className="space-y-4">
                <div>
                  <label className="text-[10px] font-bold text-slate-400 uppercase block mb-1">Full Name</label>
                  <input required type="text" placeholder="Enter full name" className="w-full border rounded p-2 text-sm outline-none focus:ring-1 focus:ring-indigo-500" value={newEmp.name} onChange={e => setNewEmp({...newEmp, name: e.target.value})} />
                </div>
                
                <div>
                  <label className="text-[10px] font-bold text-slate-400 uppercase block mb-1">Official Email</label>
                  <input required type="email" placeholder="email@company.com" className="w-full border rounded p-2 text-sm outline-none focus:ring-1 focus:ring-indigo-500" value={newEmp.email} onChange={e => setNewEmp({...newEmp, email: e.target.value})} />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-[10px] font-bold text-slate-400 uppercase block mb-1">Employee ID</label>
                    <input required type="text" placeholder="Unique ID" className="w-full border rounded p-2 text-sm outline-none focus:ring-1 focus:ring-indigo-500" value={newEmp.id} onChange={e => setNewEmp({...newEmp, id: e.target.value})} />
                  </div>
                  <div>
                    <label className="text-[10px] font-bold text-slate-400 uppercase block mb-1">Mobile</label>
                    <input required type="text" placeholder="Phone number" className="w-full border rounded p-2 text-sm outline-none focus:ring-1 focus:ring-indigo-500" value={newEmp.mobile} onChange={e => setNewEmp({...newEmp, mobile: e.target.value})} />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-[10px] font-bold text-slate-400 uppercase block mb-1">Department</label>
                    <select className="w-full border rounded p-2 text-sm bg-white outline-none focus:ring-1 focus:ring-indigo-500" value={newEmp.dept} onChange={e => setNewEmp({...newEmp, dept: e.target.value})}>
                      <option>Engineering</option>
                      <option>Product</option>
                      <option>Marketing</option>
                      <option>Sales</option>
                      <option>HR</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] font-bold text-slate-400 uppercase block mb-1">Gender</label>
                    <select className="w-full border rounded p-2 text-sm bg-white outline-none focus:ring-1 focus:ring-indigo-500" value={newEmp.gender} onChange={e => setNewEmp({...newEmp, gender: e.target.value})}>
                      <option>Male</option>
                      <option>Female</option>
                      <option>Other</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="text-[10px] font-bold text-slate-400 uppercase block mb-1">Designation / Role</label>
                  <input required type="text" placeholder="Role description" className="w-full border rounded p-2 text-sm outline-none focus:ring-1 focus:ring-indigo-500" value={newEmp.role} onChange={e => setNewEmp({...newEmp, role: e.target.value})} />
                </div>

                <button type="submit" disabled={loading} className="w-full bg-indigo-600 text-white font-bold py-2.5 rounded shadow-lg hover:bg-indigo-700 transition-all active:scale-[0.98] disabled:bg-slate-300">
                  {loading ? 'Processing...' : 'Onboard Employee'}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeDirectory;